import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import ItemListEditor from './ItemListEditor';
import {
  getAdminPartnerDetail, updatePartner, isPartnerApiUnavailable, PARTNER_TYPE_LABEL, partnerTypes,
} from '../../../api/partnerApi';

/**
 * 종류별로 어떤 하위 목록을 보여줄지, 각 목록의 필드가 무엇인지 한곳에 모은다.
 * 화면을 다섯 벌 만들지 않고 이 표만 고치면 되도록 스키마로 관리한다.
 */
const SECTIONS = {
  filmography: {
    title: '필모그래피',
    description: '스틸은 가로형 16:9 권장. 올리면 800px로 자동 축소됩니다.',
    addLabel: '작품 추가',
    fields: [
      { key: 'imageFileId', urlKey: 'imageUrl', type: 'image', label: '스틸', width: '0 0 auto' },
      { key: 'title', label: '작품명', width: '2 1 200px' },
      { key: 'year', label: '연도', type: 'number', width: '0 1 100px' },
      { key: 'category', label: '분류', placeholder: '영화 / 웹드라마', width: '1 1 140px' },
    ],
  },
  talents: {
    title: '소속 배우',
    description: '캐스타에 가입하지 않은 배우도 등록할 수 있습니다. 계정 연결은 비워두고 이름·사진만 넣으면 됩니다.',
    addLabel: '배우 추가',
    fields: [
      { key: 'profileImageFileId', urlKey: 'profileImageUrl', type: 'image', label: '사진', width: '0 0 auto' },
      { key: 'name', label: '이름', width: '1 1 140px' },
      { key: 'roleSummary', label: '대표작·역할', width: '2 1 200px' },
      {
        key: 'talentProfileId',
        label: '캐스타 계정 연결',
        // 번호를 직접 넣으면 한 자리만 틀려도 엉뚱한 배우로 연결된다
        type: 'talent',
        width: '1 1 240px',
        hint: '가입 배우만. 비우면 링크 없이 이름·사진만 표시',
      },
    ],
  },
  departments: {
    title: '학과 · 학회',
    addLabel: '항목 추가',
    fields: [
      { key: 'name', label: '이름', width: '2 1 200px' },
      { key: 'kind', label: '구분', placeholder: '학과 / 학회', width: '1 1 140px' },
    ],
  },
  works: {
    title: '작품',
    addLabel: '작품 추가',
    fields: [
      { key: 'imageFileId', urlKey: 'imageUrl', type: 'image', label: '이미지', width: '0 0 auto' },
      { key: 'title', label: '작품명', width: '2 1 200px' },
      { key: 'year', label: '연도', type: 'number', width: '0 1 100px' },
    ],
  },
  benefits: {
    title: '할인 혜택',
    addLabel: '혜택 추가',
    fields: [
      { key: 'title', label: '혜택명', width: '2 1 200px' },
      { key: 'discount', label: '할인율', placeholder: '20% 할인', width: '0 1 130px' },
      { key: 'condition', label: '조건', placeholder: '캐스타 회원 인증 시', width: '1 1 180px' },
      { key: 'description', label: '설명', type: 'textarea' },
    ],
  },
  gallery: {
    title: '갤러리',
    description: '가로형 4:3 권장. 올리면 800px로 자동 축소됩니다.',
    addLabel: '이미지 추가',
    fields: [
      { key: 'imageFileId', urlKey: 'url', type: 'image', label: '이미지', width: '0 0 auto' },
      { key: 'caption', label: '설명', width: '2 1 240px' },
    ],
  },
};

/**
 * 여러 종류를 가진 파트너는 각 종류의 섹션을 합쳐서 편집한다.
 * 순서는 SECTIONS 선언 순서를 따르고, 겹치는 섹션은 한 번만 나온다.
 */
export function visibleSectionsOf(partner) {
  const kinds = new Set(
    partnerTypes(partner).flatMap(type => SECTIONS_BY_TYPE[type] ?? []),
  );
  return Object.keys(SECTIONS).filter(k => kinds.has(k));
}

/** 종류별로 노출할 섹션 */
const SECTIONS_BY_TYPE = {
  PRODUCTION: ['filmography', 'talents'],
  AGENCY: ['talents'],
  ACADEMY: ['departments', 'talents', 'works'],
  ETC: ['benefits', 'gallery'],
};

export default function AdminPartnerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [lists, setLists] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getAdminPartnerDetail(id);
      setPartner(d);
      const next = {};
      Object.keys(SECTIONS).forEach(k => { next[k] = Array.isArray(d?.[k]) ? d[k] : []; });
      setLists(next);
    } catch (err) {
      setError(isPartnerApiUnavailable(err)
        ? '파트너 API가 아직 배포되지 않았습니다.'
        : `불러오지 못했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (saving || !partner) return;
    setSaving(true);
    setError(null);
    try {
      // 하위 목록은 통째로 교체된다. 순서는 배열 순서 그대로 sortOrder가 된다.
      const visible = visibleSectionsOf(partner);

      // 서버가 돌려준 객체를 그대로 되보내면 createdAt 같은 읽기 전용 필드가 섞여
      // 알 수 없는 필드로 거부될 수 있다. 편집 대상만 명시적으로 담는다.
      const payload = {
        id: partner.id,
        types: partnerTypes(partner),
        name: partner.name,
        slug: partner.slug ?? null,
        tagline: partner.tagline ?? null,
        region: partner.region ?? null,
        logoFileId: partner.logoFileId ?? null,
        homepage: partner.homepage ?? null,
        email: partner.email ?? null,
        instagram: partner.instagram ?? null,
        youtube: partner.youtube ?? null,
        description: partner.description ?? null,
      };

      Object.keys(SECTIONS).forEach(k => {
        // 이 종류에 없는 목록은 키 자체를 넣지 않는다 (서버가 건드리지 않음).
        // 빈 배열로 보내면 '전부 삭제'가 되어 종류 변경 시 다른 목록이 날아간다.
        if (!visible.includes(k)) return;

        const urlKeys = SECTIONS[k].fields.filter(f => f.type === 'image').map(f => f.urlKey);
        // 배우 연결(talent)도 값은 숫자다
        const numberKeys = SECTIONS[k].fields
          .filter(f => f.type === 'number' || f.type === 'talent')
          .map(f => f.key);

        payload[k] = (lists[k] ?? []).map((item, i) => {
          const clean = { ...item, sortOrder: i };
          // 미리보기 전용 URL은 서버로 보내지 않는다
          urlKeys.forEach(uk => delete clean[uk]);
          // input 값은 항상 문자열이다. 미가입 배우처럼 비워둔 칸을 ''로 보내면
          // 숫자 필드에서 거부되므로 값 없음으로 바꿔준다.
          numberKeys.forEach(nk => {
            const v = clean[nk];
            clean[nk] = v === '' || v === null || v === undefined ? null : Number(v);
          });
          return clean;
        });
      });
      await updatePartner(partner.id, payload);
      setSavedAt(new Date());
      load();
    } catch (err) {
      setError(`저장에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setSaving(false);
    }
  };

  const visibleSections = partner ? visibleSectionsOf(partner) : [];

  return (
    <AdminLayout pageId="partners">
      <div className="admin-topbar">
        <h1>{partner?.name ?? '파트너'} 상세</h1>
        <div className="admin-topbar__right">
          {partner && (
            <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {partnerTypes(partner).map(t => PARTNER_TYPE_LABEL[t]).join(' · ')}
            </span>
          )}
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button className="ag-btn ag-btn--sm" onClick={() => navigate('/admin/partners')}>
          <i className="ti ti-arrow-left" /> 목록
        </button>
        {partner && (
          <button className="ag-btn ag-btn--sm" onClick={() => window.open(`/partners/${partner.slug ?? partner.id}`, '_blank')}>
            <i className="ti ti-external-link" /> 공개 페이지 보기
          </button>
        )}
        <div style={{ flex: 1 }} />
        {savedAt && (
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', alignSelf: 'center' }}>
            {savedAt.getHours()}:{String(savedAt.getMinutes()).padStart(2, '0')} 저장됨
          </span>
        )}
        <button className="ag-btn ag-btn--primary" onClick={handleSave} disabled={saving || !partner}>
          <i className="ti ti-device-floppy" /> {saving ? '저장 중...' : '전체 저장'}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {loading ? (
        <div className="ag-card" style={{ fontSize: 13 }}>불러오는 중...</div>
      ) : !partner ? (
        <div className="ag-card" style={{ fontSize: 13 }}>파트너를 찾을 수 없습니다.</div>
      ) : visibleSections.length === 0 ? (
        <div className="ag-card" style={{ fontSize: 13 }}>이 종류에는 관리할 하위 목록이 없습니다.</div>
      ) : (
        visibleSections.map(key => (
          <ItemListEditor
            key={key}
            title={SECTIONS[key].title}
            description={SECTIONS[key].description}
            addLabel={SECTIONS[key].addLabel}
            fields={SECTIONS[key].fields}
            items={lists[key] ?? []}
            onChange={(next) => setLists(prev => ({ ...prev, [key]: next }))}
          />
        ))
      )}

      {partner && visibleSections.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
          저장하면 각 목록이 화면에 보이는 순서 그대로 교체됩니다. 삭제한 항목은 저장 시 함께 사라집니다.
        </p>
      )}
    </AdminLayout>
  );
}
